import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Contraseña actualizada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
          <p className="text-white/60">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="orb orb-brand w-[600px] h-[600px] -top-[200px] -left-[200px] animate-float-slow" style={{ opacity: 0.4 }} />
        </div>
        
        <div className="w-full max-w-md relative z-10 animate-fade-in-up">
          <div className="text-center mb-8">
            <img src="/logo-dark.png" alt="FASCINAI ONE" className="h-14 object-contain mx-auto mb-6" />
          </div>
          
          <div className="glass-dark rounded-2xl p-8 border border-white/5 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Enlace inválido o expirado
            </h3>
            <p className="text-white/60 text-sm mb-6">
              Este enlace de recuperación ya no es válido. Por favor solicita uno nuevo.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-brand hover:bg-brand/90 text-black font-semibold">
                Solicitar nuevo enlace
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="orb orb-brand w-[600px] h-[600px] -top-[200px] -left-[200px] animate-float-slow" style={{ opacity: 0.4 }} />
        </div>
        
        <div className="w-full max-w-md relative z-10 animate-fade-in-up">
          <div className="text-center mb-8">
            <img src="/logo-dark.png" alt="FASCINAI ONE" className="h-14 object-contain mx-auto mb-6" />
          </div>
          
          <div className="glass-dark rounded-2xl p-8 border border-white/5 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              ¡Contraseña actualizada!
            </h3>
            <p className="text-white/60 text-sm mb-6">
              Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
            <Link to="/login">
              <Button className="bg-brand hover:bg-brand/90 text-black font-semibold">
                Ir al login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="orb orb-brand w-[600px] h-[600px] -top-[200px] -left-[200px] animate-float-slow" style={{ opacity: 0.4 }} />
        <div className="orb orb-lavender w-[500px] h-[500px] -bottom-[150px] -right-[150px] animate-float-slower" style={{ opacity: 0.3, animationDelay: '2s' }} />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img src="/logo-dark.png" alt="FASCINAI ONE" className="h-14 object-contain" />
          </div>
          <p className="text-white/60 text-lg">Nueva contraseña</p>
        </div>

        <div className="glass-dark rounded-2xl p-8 border border-white/5">
          <form onSubmit={handleSubmit} className="space-y-6">
            <p className="text-white/70 text-sm text-center mb-6">
              Ingresa tu nueva contraseña para <strong className="text-white">{email}</strong>
            </p>

            {/* New Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 text-sm">
                Nueva contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full pl-12 pr-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl focus:border-brand focus:ring-brand/20"
                  data-testid="reset-password-new"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80 text-sm">
                Confirmar contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite tu contraseña"
                  className="w-full pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl focus:border-brand focus:ring-brand/20"
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            {/* Password strength indicator */}
            {password && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        password.length >= (i + 1) * 3
                          ? i < 2 ? 'bg-red-500' : i < 3 ? 'bg-yellow-500' : 'bg-green-500'
                          : 'bg-white/10'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-white/40">
                  {password.length < 6 ? 'Muy corta' : password.length < 9 ? 'Aceptable' : 'Fuerte'}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-brand hover:bg-brand/90 text-black font-semibold rounded-xl transition-all duration-200"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Restablecer contraseña'
              )}
            </Button>

            {/* Back to Login */}
            <div className="text-center pt-4">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-white/60 hover:text-brand transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
